import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Tag, Heart, ImagePlus, Plus, Trash2, Gavel, ShoppingBag, Calendar, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";

const CAT_EMOJI = {
  "Modest Fashion": "🧕", "Beauty & Fragrance": "💄", "Islamic Products": "☪️", "Fundraising": "🤲",
  "Jewelry & Accessories": "💎", "Halal Food": "🍔", "Halal Cooking": "🍳", "Home & Kitchen": "🏠", "Kids & Family": "🧸", "Electronics": "📱",
};
const TIMERS = [30, 45, 60, 90];

// Warm campaign covers auto-suggested for fundraiser hosts who skip the upload.
const FUNDRAISER_COVERS = [
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?crop=entropy&cs=srgb&fm=jpg&w=1280&q=80",
  "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&w=1280&q=80",
  "https://images.unsplash.com/photo-1651372381086-9861c9c81db5?crop=entropy&cs=srgb&fm=jpg&w=1280&q=80",
];

function newItem() {
  return { key: Math.random().toString(36).slice(2), title: "", type: "buynow", price: "", timer: 60, image: null, requires_shipping: false };
}

async function fileToScaledDataUrl(file, max = 1280) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function CreateShow() {
  const navigate = useNavigate();
  const coverInput = useRef(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [saleType, setSaleType] = useState("marketplace");
  const [goal, setGoal] = useState("");
  const [cover, setCover] = useState(null);
  const [suggestedCover] = useState(() => FUNDRAISER_COVERS[Math.floor(Math.random() * FUNDRAISER_COVERS.length)]);
  const [category, setCategory] = useState("");
  const [when, setWhen] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [items, setItems] = useState([newItem()]);

  useEffect(() => { api.get("/categories").then((d) => setCats(d.categories.filter((c) => c !== "All"))).catch(() => {}); }, []);

  const updateItem = (key, patch) => setItems((its) => its.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setItems((its) => (its.length > 1 ? its.filter((it) => it.key !== key) : its));

  const onCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image");
    try { setCover(await fileToScaledDataUrl(f)); } catch { toast.error("Couldn't read that image"); }
  };

  const onItemImage = async (key, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { updateItem(key, { image: await fileToScaledDataUrl(f, 900) }); } catch { toast.error("Couldn't read image"); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Add a show title");
    if (saleType !== "fundraiser" && !category) return toast.error("Pick a category");
    if (when === "schedule") {
      if (!scheduledAt) return toast.error("Pick a date and time for your show");
      if (new Date(scheduledAt).getTime() <= Date.now()) return toast.error("Scheduled time must be in the future");
    }
    const clean = items.filter((it) => it.title.trim());
    for (const it of clean) {
      if (!it.price || Number(it.price) <= 0) return toast.error(`Set a price for "${it.title}"`);
    }
    setSaving(true);
    const effectiveCover = cover || (saleType === "fundraiser" ? suggestedCover : null);
    try {
      const res = await api.post("/shows", {
        title, category: saleType === "fundraiser" ? "Fundraising" : category, sale_type: saleType, cover: effectiveCover,
        status: when === "now" ? "live" : "scheduled",
        scheduled_at: when === "schedule" ? new Date(scheduledAt).toISOString() : null,
        goal_amount: saleType === "fundraiser" && goal ? Number(goal) : null,
        items: clean.map((it) => ({
          title: it.title.trim(), type: saleType === "fundraiser" ? "donation" : it.type,
          price: Number(it.price), timer: it.type === "auction" ? Number(it.timer) : null, image: it.image,
          requires_shipping: saleType === "fundraiser" ? !!it.requires_shipping : undefined,
        })),
      });
      toast.success(when === "now" ? "You're live! Redirecting…" : "Show scheduled! Redirecting…");
      navigate(`/show/${res.show.id}`);
    } catch (err) {
      toast.error(err.message || "Could not create show");
    } finally { setSaving(false); }
  };

  const Toggle = ({ active, onClick, icon: Icon, label, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`gl-toggle ${active ? "active" : ""}`}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="fade-up" style={{ maxWidth: 720, margin: "0 auto", paddingBottom: 90 }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 2 }}>Start a Show</h1>
      <p className="ss-muted" style={{ marginBottom: 18 }}>Set up your live sale and go on air.</p>

      {/* Launch pricing banner */}
      <div style={{ background: "var(--brand)", color: "#fff", borderRadius: 16, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }} data-testid="launch-banner">
        <div style={{ width: 44, height: 44, borderRadius: 999, background: "var(--gold)", color: "#3a2a00", display: "grid", placeItems: "center", fontWeight: 800, flexShrink: 0 }}>8%</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800 }}>Launch pricing on all sales 🎉</div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>Keep more of every sale — just 8% seller fee. Limited-time launch pricing.</div>
        </div>
        <Sparkles size={20} style={{ color: "var(--gold)" }} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        {/* Title */}
        <div>
          <label className="ss-label">Show title</label>
          <div className="ss-field"><input placeholder="e.g. Rare Oud & Attar Drop 🌙" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="show-title-input" /></div>
        </div>

        {/* Show type */}
        <div>
          <label className="ss-label">Show type</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Toggle active={saleType === "marketplace"} onClick={() => setSaleType("marketplace")} icon={Tag} label="Marketplace" testid="saletype-marketplace" />
            <Toggle active={saleType === "fundraiser"} onClick={() => setSaleType("fundraiser")} icon={Heart} label="Fundraiser" testid="saletype-fundraiser" />
          </div>
        </div>

        {/* Fundraising goal (fundraiser only) */}
        {saleType === "fundraiser" && (
          <div data-testid="goal-block">
            <label className="ss-label">Fundraising goal ($)</label>
            <div className="ss-field">
              <span className="ss-muted">$</span>
              <input type="number" min="0" step="100" placeholder="e.g. 10000" value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="goal-input" />
            </div>
            <div className="ss-muted" style={{ fontSize: 12, marginTop: 4 }}>Shown as a live progress bar. Donors can give any amount or pick a perk below.</div>
          </div>
        )}

        {/* Cover photo */}
        <div>
          <label className="ss-label">Cover photo</label>
          <input ref={coverInput} type="file" accept="image/*" onChange={onCover} style={{ display: "none" }} data-testid="cover-input" />
          <button type="button" onClick={() => coverInput.current?.click()} data-testid="cover-upload"
            style={{ width: "100%", aspectRatio: "16/9", borderRadius: 14, border: "2px dashed var(--line)", background: cover ? `center/cover no-repeat url(${cover})` : (saleType === "fundraiser" ? `center/cover no-repeat url(${suggestedCover})` : "var(--surface-2, transparent)"), display: "grid", placeItems: "center", cursor: "pointer", overflow: "hidden", position: "relative" }}>
            {!cover && saleType !== "fundraiser" && (
              <div style={{ textAlign: "center", color: "var(--muted)" }}>
                <ImagePlus size={28} />
                <div style={{ fontWeight: 700, marginTop: 6 }}>Upload cover photo</div>
                <div style={{ fontSize: 12 }}>Recommended 16:9 · optional</div>
              </div>
            )}
            {!cover && saleType === "fundraiser" && (
              <div data-testid="suggested-cover-note" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 14, background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,.55) 100%)", color: "#fff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, alignSelf: "flex-start", background: "rgba(0,0,0,.45)", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 700 }}>
                  <ImagePlus size={14} /> Suggested cover — tap to upload your own
                </div>
              </div>
            )}
            {cover && <span onClick={(e) => { e.stopPropagation(); setCover(null); }} className="disc-search" style={{ position: "absolute", top: 8, right: 8, width: 30, height: 30 }}><X size={16} /></span>}
          </button>
        </div>

        {/* Category */}
        {saleType !== "fundraiser" && (
        <div>
          <label className="ss-label">Category</label>
          <select className="ss-field" style={{ width: "100%" }} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="show-category-select">
            <option value="">Select a category</option>
            {cats.map((c) => <option key={c} value={c}>{`${CAT_EMOJI[c] || ""} ${c}`}</option>)}
          </select>
        </div>
        )}

        {/* When */}
        <div>
          <label className="ss-label">When</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Toggle active={when === "now"} onClick={() => setWhen("now")} icon={Radio} label="Go Live Now" testid="when-now" />
            <Toggle active={when === "schedule"} onClick={() => setWhen("schedule")} icon={Calendar} label="Schedule" testid="when-schedule" />
          </div>
          {when === "schedule" && (
            <div style={{ marginTop: 12 }}>
              <label className="ss-label">Date & time it airs</label>
              <div className="ss-field" style={{ height: 44 }}>
                <Calendar size={16} className="ss-muted" />
                <input type="datetime-local" value={scheduledAt} min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)} onChange={(e) => setScheduledAt(e.target.value)} data-testid="schedule-datetime" style={{ colorScheme: "light" }} />
              </div>
              <div className="ss-muted" style={{ fontSize: 12, marginTop: 6 }}>Buyers will see when your show goes live. You can start early anytime with “Go Live”.</div>
            </div>
          )}
        </div>

        {/* Items / Lots */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <label className="ss-label" style={{ margin: 0 }}>{saleType === "fundraiser" ? "Donation perks / tiers" : "Items / Lots"}</label>
            <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setItems((its) => [...its, newItem()])} data-testid="add-item"><Plus size={14} /> {saleType === "fundraiser" ? "Add perk" : "Add item"}</button>
          </div>
          {saleType === "fundraiser" && <div className="ss-muted" style={{ fontSize: 12, marginBottom: 8 }}>e.g. “Buy a Brick — $50”, “Sponsor a meal — $10”. Optional — donors can also give a custom amount.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map((it, idx) => {
              const imgId = `item-img-${it.key}`;
              const isFund = saleType === "fundraiser";
              return (
                <div className="ss-card" style={{ padding: 14 }} key={it.key} data-testid={`item-row-${idx}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontWeight: 800, color: "var(--muted)" }}>#{idx + 1}</span>
                    <input placeholder={isFund ? "Perk name (e.g. Buy a Brick)" : "Item title"} value={it.title} onChange={(e) => updateItem(it.key, { title: e.target.value })} data-testid={`item-title-${idx}`}
                      style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 15, fontWeight: 700, color: "var(--text)" }} />
                    {items.length > 1 && <button type="button" className="disc-search" style={{ width: 30, height: 30 }} onClick={() => removeItem(it.key)} data-testid={`item-remove-${idx}`}><Trash2 size={15} /></button>}
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    {!isFund && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button type="button" className={`gl-chip ${it.type === "auction" ? "active" : ""}`} onClick={() => updateItem(it.key, { type: "auction" })} data-testid={`item-auction-${idx}`}><Gavel size={13} /> Auction</button>
                        <button type="button" className={`gl-chip ${it.type === "buynow" ? "active" : ""}`} onClick={() => updateItem(it.key, { type: "buynow" })} data-testid={`item-buynow-${idx}`}><ShoppingBag size={13} /> Buy Now</button>
                      </div>
                    )}
                    <div className="ss-field" style={{ width: isFund ? 200 : 140, height: 40 }}>
                      <span className="ss-muted">$</span>
                      <input type="number" min="0" step="0.01" placeholder={isFund ? "Donation amount" : it.type === "auction" ? "Start bid" : "Price"} value={it.price} onChange={(e) => updateItem(it.key, { price: e.target.value })} data-testid={`item-price-${idx}`} style={{ minWidth: 0, flex: 1 }} />
                    </div>
                    {!isFund && it.type === "auction" && (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span className="ss-muted" style={{ fontSize: 13 }}>Timer</span>
                        {TIMERS.map((t) => (
                          <button type="button" key={t} className={`gl-chip ${it.timer === t ? "active" : ""}`} onClick={() => updateItem(it.key, { timer: t })} data-testid={`item-timer-${idx}-${t}`}>{t}s</button>
                        ))}
                      </div>
                    )}
                    <input id={imgId} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => onItemImage(it.key, e)} />
                    <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => document.getElementById(imgId).click()} data-testid={`item-image-${idx}`}>
                      {it.image ? <img src={it.image} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: "cover" }} /> : <ImagePlus size={14} />} Photo
                    </button>
                    {isFund && (
                      <button type="button" className={`gl-chip ${it.requires_shipping ? "active" : ""}`} onClick={() => updateItem(it.key, { requires_shipping: !it.requires_shipping })} data-testid={`item-ship-${idx}`}>
                        <ShoppingBag size={13} /> {it.requires_shipping ? "Ships to donor" : "No shipping"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sticky action */}
      <div className="create-live-bar">
        <div className="create-live-bar-inner">
          <button className={`ss-btn ss-btn-block ${when === "now" ? "ss-btn-live" : "ss-btn-primary"}`} onClick={submit} disabled={saving} data-testid="create-show-submit">
            <Radio size={16} /> {saving ? "Please wait…" : when === "now" ? "Go Live" : "Schedule Show"}
          </button>
        </div>
      </div>
    </div>
  );
}
